/**
 * Version polling via version.json.
 *
 * Each build generates a version.json with `id: ops-<gitHash>`.
 * This module polls it every 5 minutes, compares the id against the
 * current build, and triggers reload via 3 mechanisms:
 * - Route change: AppShell checks isPendingUpdate() on navigation
 * - Lazy load error: lazyWithRetry checks isPendingUpdate() on chunk failure
 * - Idle timeout: auto-reload after 5 min of inactivity
 */

const TAG = '[sw-update]';
const POLL_INTERVAL = Number(import.meta.env.VITE_VERSION_POLL_INTERVAL_MS) || 60 * 1000;
const IDLE_TIMEOUT = Number(import.meta.env.VITE_VERSION_IDLE_RELOAD_MS) || 8 * 60 * 60 * 1000;
const ACTIVITY_THROTTLE = 2000;
const CURRENT_VERSION_ID = `ops-${__GIT_COMMIT_HASH__}`;

let hasPendingUpdate = false;

/** Check if a new version has been detected and reload is pending. */
export function isPendingUpdate(): boolean {
  return hasPendingUpdate;
}

/**
 * Reload that respects beforeunload handlers (e.g. Designer unsaved changes).
 * Dispatches a synthetic beforeunload event — if any listener calls
 * preventDefault(), the reload is skipped silently.
 */
function safeReload() {
  const event = new Event('beforeunload', { cancelable: true });
  const allowed = window.dispatchEvent(event);
  if (!allowed) {
    console.warn(`${TAG} Reload blocked by beforeunload handler, will retry on next idle check`);
    return;
  }
  window.location.reload();
}

/** Track user activity and auto-reload after idle timeout. */
function startIdleDetection() {
  let lastRecorded = Date.now();
  let idleTimer: ReturnType<typeof setTimeout>;

  const scheduleReload = () => {
    clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      console.warn(`${TAG} User idle for ${IDLE_TIMEOUT / 1000}s, reloading for new version`);
      safeReload();
      // If safeReload was blocked by beforeunload, re-schedule
      scheduleReload();
    }, IDLE_TIMEOUT);
  };

  const recordActivity = () => {
    const now = Date.now();
    if (now - lastRecorded < ACTIVITY_THROTTLE) return;
    lastRecorded = now;
    scheduleReload();
  };

  const events = ['mousemove', 'keydown', 'scroll', 'click', 'touchstart'] as const;
  for (const evt of events) {
    document.addEventListener(evt, recordActivity, { passive: true });
  }

  // Start the first timer immediately
  scheduleReload();
}

/** Sets pending update flag and starts idle detection. */
function setPendingUpdate() {
  if (hasPendingUpdate) return;
  hasPendingUpdate = true;
  startIdleDetection();
}

async function checkVersion() {
  try {
    // Cache-bust to avoid getting a cached version file
    const url = `${import.meta.env.VITE_SCIO3_CDN_URL || '/'}version.json?_t=${Date.now()}`;
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) return;

    const version = await res.json();
    const remoteId = version.id;

    if (remoteId && remoteId !== CURRENT_VERSION_ID) {
      console.warn(`${TAG} New build detected: ${CURRENT_VERSION_ID} → ${remoteId}`);
      setPendingUpdate();
    }
  } catch {
    // Network error — silently ignore, will retry next interval
  }
}

/** Start polling version.json for version changes. */
export function startVersionPolling() {
  // Skip polling in dev — version.json doesn't exist
  if (import.meta.env.DEV) return;

  // Initial check after 30s (let app fully bootstrap first)
  setTimeout(checkVersion, 30_000);
  // Then every 5 minutes
  setInterval(checkVersion, POLL_INTERVAL);
}
